import * as React from 'react';
import {Search, SquareX} from 'lucide-react';
import {useTranslation} from 'react-i18next';
import {cn} from '../../lib/cn';
import {RotateArrow} from '../../rotate-arrow';

type ComboboxTriggerProps = {
  triggerId: string;
  listboxId: string;
  labelId: string;
  valueId: string;
  describedErrorId?: string;
  accessibleLabel?: string;
  isOpen: boolean;
  isFocused: boolean;
  isBlocked: boolean;
  hasValue: boolean;
  isEmpty: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  loading?: boolean;
  invalid?: boolean;
  isMulti?: boolean;
  inputRef: React.Ref<HTMLInputElement>;
  inputValue: string;
  placeholder?: string;
  showPlaceholderWhenIdle?: boolean;
  highlightedIndex?: number;
  getOptionId?: (index: number) => string;
  onInputChange: React.ChangeEventHandler<HTMLInputElement>;
  onInputFocus: React.FocusEventHandler<HTMLInputElement>;
  onInputKeyDown: React.KeyboardEventHandler<HTMLInputElement>;
  onContainerClick: () => void;
  showClear?: boolean;
  onClear?: (event: React.MouseEvent) => void;
  clearLabel?: string;
  leadingContent?: React.ReactNode;
  leftIcon?: React.ReactNode;
  containerClassName?: string;
  inputClassName?: string;
  hideIndicator?: boolean;
  autoFocus?: boolean;
  searchable?: boolean;
};

function ComboboxTrigger({
  triggerId,
  listboxId,
  labelId,
  valueId,
  describedErrorId,
  accessibleLabel,
  isOpen,
  isFocused,
  isBlocked,
  hasValue,
  isEmpty,
  disabled,
  readOnly,
  loading,
  invalid,
  isMulti,
  inputRef,
  inputValue,
  placeholder,
  showPlaceholderWhenIdle,
  highlightedIndex,
  getOptionId,
  onInputChange,
  onInputFocus,
  onInputKeyDown,
  onContainerClick,
  showClear,
  onClear,
  clearLabel,
  leadingContent,
  leftIcon,
  containerClassName,
  inputClassName,
  hideIndicator,
  autoFocus,
  searchable = true,
}: ComboboxTriggerProps) {
  const {t} = useTranslation();
  const resolvedPlaceholder = placeholder ?? t('type_to_search');
  const visiblePlaceholder =
    searchable && (showPlaceholderWhenIdle || isFocused || isOpen)
      ? resolvedPlaceholder
      : '';
  const inputMeasureValue = inputValue || visiblePlaceholder || ' ';
  const activeOptionId =
    isOpen && highlightedIndex !== undefined && highlightedIndex >= 0 && getOptionId
      ? getOptionId(highlightedIndex)
      : undefined;

  return (
    <div
      role="combobox"
      aria-haspopup="listbox"
      aria-expanded={isOpen}
      aria-controls={listboxId}
      aria-label={accessibleLabel}
      aria-labelledby={
        accessibleLabel
          ? undefined
          : hasValue && valueId
            ? `${labelId} ${valueId}`
            : labelId
      }
      aria-describedby={describedErrorId}
      aria-invalid={invalid}
      aria-busy={loading}
      aria-disabled={isBlocked}
      onClick={onContainerClick}
      className={cn(
        'relative box-border flex w-full rounded-[6px] border-0 text-left text-[16px] font-medium leading-5 outline-none transition-colors duration-200',
        searchable ? 'cursor-text' : 'cursor-pointer',
        'min-h-12',
        isEmpty && !isFocused ? 'bg-[var(--empty-field-background)]' : 'bg-transparent',
        readOnly && !disabled && !loading && 'cursor-default',
        disabled && !loading && 'cursor-not-allowed',
        loading && '!cursor-progress',
        containerClassName,
      )}
    >
      {leftIcon && (
        <span className="pointer-events-none absolute left-0 top-0 flex h-full max-w-10 items-center justify-center text-[var(--chekin-color-gray-2)]">
          <span className="flex h-full w-10 items-center justify-center">{leftIcon}</span>
        </span>
      )}
      {!leftIcon && !isMulti && searchable && (isFocused || isOpen) && (
        <span className="pointer-events-none absolute left-0 top-0 flex h-full max-w-10 items-center justify-center text-[var(--chekin-color-gray-2)]">
          <span className="flex h-full w-10 items-center justify-center">
            <Search size={16} aria-hidden="true" />
          </span>
        </span>
      )}
      <div
        className={cn(
          'flex min-w-0 flex-1 items-center gap-2 py-[10px]',
          leftIcon || (searchable && !isMulti && (isFocused || isOpen)) ? 'pl-9' : 'pl-4',
          isMulti && 'flex-wrap',
        )}
      >
        {leadingContent}
        <span
          className={cn(
            'grid min-w-0 max-w-full',
            isMulti ? 'w-min flex-none min-w-[40px]' : 'w-full overflow-hidden',
            !searchable && 'sr-only',
          )}
        >
          <span
            aria-hidden="true"
            className="invisible col-start-1 row-start-1 whitespace-pre text-[16px] font-medium leading-5"
          >
            {inputMeasureValue}
          </span>
          <input
            ref={inputRef}
            id={triggerId}
            type="text"
            value={inputValue}
            onChange={onInputChange}
            onFocus={onInputFocus}
            onKeyDown={onInputKeyDown}
            disabled={isBlocked}
            readOnly={readOnly || !searchable}
            placeholder={visiblePlaceholder}
            autoComplete="off"
            autoFocus={autoFocus}
            aria-autocomplete="list"
            aria-controls={listboxId}
            aria-activedescendant={activeOptionId}
            className={cn(
              'col-start-1 row-start-1 m-0 box-border w-full min-w-0 border-0 bg-transparent p-0 text-[16px] font-medium leading-5 text-[var(--chekin-color-brand-navy)] outline-none placeholder:text-[length:var(--field-placeholder-font-size)] placeholder:font-[var(--field-placeholder-font-weight)] placeholder:text-[var(--chekin-color-gray-1)]',
              isMulti ? 'min-w-[40px]' : 'text-ellipsis',
              readOnly && !disabled && !loading && 'cursor-default',
              disabled && !loading && 'cursor-not-allowed',
              loading && '!cursor-progress',
              inputClassName,
            )}
          />
        </span>
      </div>
      <span className="flex h-12 shrink-0 items-center gap-2 self-start pl-2 pr-4 text-[var(--chekin-color-gray-2)]">
        {showClear && (
          <button
            type="button"
            onClick={onClear}
            onMouseDown={event => event.stopPropagation()}
            className="flex h-5 w-5 items-center justify-center rounded-[3px] border-0 bg-transparent p-0 text-[var(--chekin-neutral-500)] hover:shadow-[0_3px_3px_#0f477734]"
            aria-label={clearLabel}
          >
            <SquareX
              size={15}
              fill="var(--chekin-neutral-500)"
              color="#f8f8f8"
              strokeWidth={1.8}
            />
          </button>
        )}
        {!hideIndicator && (
          <RotateArrow
            shouldRotate={isOpen}
            className={cn(
              isFocused || isOpen
                ? 'text-[var(--chekin-color-brand-blue)]'
                : 'text-[var(--chekin-color-gray-2)]',
            )}
          />
        )}
      </span>
    </div>
  );
}

export {ComboboxTrigger};
export type {ComboboxTriggerProps};
